import { useCallback, useState } from 'preact/hooks';
import { getSlopeColorHex, SLOPE_3D_LEGEND } from './slopeThreeColors.js';
import ParcelSlopeThreeScene from './ParcelSlopeThreeScene.jsx';
import ParcelSlopeThreeSectionChart from './ParcelSlopeThreeSectionChart.jsx';
import './parcel-slope-three-card.css';

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;
}

function fmtM(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m`;
}

function fmtDeg(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Math.round(Number(v))}°`;
}

function fmtArea(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m²`;
}

export default function ParcelSlopeThreeCard({
  data = null,
  loading = false,
  error = null,
}) {
  const [labels, setLabels] = useState([]);
  const [webglOk, setWebglOk] = useState(true);
  const onLabelsChange = useCallback((next) => setLabels(next), []);
  const onUnsupported = useCallback(() => setWebglOk(false), []);

  if (loading) {
    return (
      <div className="pp-parcel-slope-three pp-parcel-slope-three--loading" aria-busy="true">
        <div className="pp-parcel-slope-three__skeleton" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="pp-parcel-slope-three pp-parcel-slope-three--error" role="alert">
        {error || '3D eğim verisi hazırlanamadı'}
      </div>
    );
  }

  const stats = data.stats || {};
  const parcel = data.parcel || {};
  const adaParsel = [parcel.ada, parcel.parsel].filter(Boolean).join(' / ');
  const rows = [
    { label: 'ortalama egim', value: fmtPct(stats.avg_slope) },
    { label: 'maksimum egim', value: fmtPct(stats.max_slope) },
    { label: 'minimum egim', value: fmtPct(stats.min_slope) },
    { label: 'en dik yon', value: fmtDeg(stats.direction_degree) },
    { label: 'yukseklik araligi', value: fmtM(stats.elevation_range) },
    { label: 'parsel alani', value: fmtArea(stats.area_m2 ?? parcel.area_m2) },
    { label: 'egim sinifi', value: stats.slope_class || '—' },
  ];

  return (
    <article className="pp-parcel-slope-three" aria-label="Parsel eğim analizi 3D mesh kartı">
      <header className="pp-parcel-slope-three__head">
        <div>
          <h2 className="pp-parcel-slope-three__title">parsel egim analizi</h2>
          <p className="pp-parcel-slope-three__subtitle">3d egim haritasi ve kesit profili · mesh</p>
        </div>
        {adaParsel ? (
          <div className="pp-parcel-slope-three__meta">
            ada/parsel:
            {' '}
            {adaParsel}
          </div>
        ) : null}
      </header>

      <div className="pp-parcel-slope-three__body">
        <div className="pp-parcel-slope-three__main">
          <div className="pp-parcel-slope-three__stage">
            {!webglOk ? (
              <div className="pp-parcel-slope-three__unsupported">
                3d gorunum desteklenmiyor (webgl yok)
              </div>
            ) : (
              <>
                <ParcelSlopeThreeScene
                  data={data}
                  onLabelsChange={onLabelsChange}
                  onUnsupported={onUnsupported}
                />
                <div className="pp-parcel-slope-three__labels" aria-hidden="true">
                  {labels.map((lb) => (
                    <span
                      key={lb.id}
                      className={`pp-parcel-slope-three__label pp-parcel-slope-three__label--${lb.tone || 'default'}`}
                      style={{ left: `${lb.x}px`, top: `${lb.y}px` }}
                    >
                      {lb.text}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <aside className="pp-parcel-slope-three__side">
          <div className="pp-parcel-slope-three__summary">
            <h3 className="pp-parcel-slope-three__panel-title">egim ozeti</h3>
            <dl className="pp-parcel-slope-three__summary-list">
              {rows.map((row) => (
                <div key={row.label} className="pp-parcel-slope-three__summary-row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="pp-parcel-slope-three__legend">
            <h3 className="pp-parcel-slope-three__panel-title">egim lejandi (%)</h3>
            <ul className="pp-parcel-slope-three__legend-list">
              {SLOPE_3D_LEGEND.map((item) => (
                <li key={item.label}>
                  <span
                    className="pp-parcel-slope-three__legend-swatch"
                    style={{ background: getSlopeColorHex((item.min + (item.max ?? 25)) / 2) }}
                  />
                  <span>
                    {item.min}
                    {' - '}
                    {item.max ?? '+'}
                    {' '}
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <footer className="pp-parcel-slope-three__footer">
        <ParcelSlopeThreeSectionChart section={data.sections?.a} />
      </footer>
    </article>
  );
}
